import { domainError, type DomainError } from '../shared/domain-error';
import type { RoleId } from '../shared/ids';
import { err, ok, type Result } from '../shared/result';
import type { RoleWithPermissions } from './permission-resolver';
import { PERMISSION_CATALOG, type KnownPermissionKey } from './permissions';
import { completePermissions, pagesOpenedBy, withTabsOfBarePages } from './pages';
import { ROLE } from './roles';

/**
 * O papel como coisa editável.
 *
 * Até aqui o papel era só a matriz do código (`ROLE_CATALOG`), copiada para o banco a
 * cada semeadura. Agora quem administra muda nome, descrição e o que ele abre — e cria
 * papéis novos. O catálogo do código passa a ser o **padrão de fábrica**: vale enquanto
 * ninguém mexeu, e é para ele que "restaurar o padrão" volta.
 */
export interface Role extends RoleWithPermissions {
  readonly name: string;
  readonly description?: string;
  /** Veio do catálogo do código. Muda, mas não sai: a semeadura o traria de volta. */
  readonly isSystem: boolean;
  /**
   * Quando alguém o editou pela última vez. A partir daí ele é de quem administra, e a
   * semeadura deixa de reescrevê-lo com o padrão do código.
   */
  readonly customizedAt?: string;
}

export const ROLE_ERROR = {
  NAME_TAKEN: 'ACCESS.ROLE_NAME_TAKEN',
  UNKNOWN_PERMISSION: 'ACCESS.ROLE_UNKNOWN_PERMISSION',
  WITHOUT_PAGE: 'ACCESS.ROLE_WITHOUT_PAGE',
  LOCKED: 'ACCESS.ROLE_LOCKED',
  IS_SYSTEM: 'ACCESS.ROLE_IS_SYSTEM',
  IN_USE: 'ACCESS.ROLE_IN_USE',
  /** O papel de Super administrador só muda de mãos pela transferência (RN-286). */
  SUPER_ADMIN_BY_TRANSFER: 'ACCESS.SUPER_ADMIN_BY_TRANSFER',
  /** Quem não é o titular não mexe no titular (RN-288). */
  SUPER_ADMIN_PROTECTED: 'ACCESS.SUPER_ADMIN_PROTECTED',
  /** O papel nunca fica sem dono: o titular não sai por desativação nem por revogação (RN-287). */
  SUPER_ADMIN_REQUIRED: 'ACCESS.SUPER_ADMIN_REQUIRED',
  /** Só o titular transfere, e só para outro perfil ativo (RN-289). */
  TRANSFER_INVALID: 'ACCESS.SUPER_ADMIN_TRANSFER_INVALID',
  /** Ninguém concede o que não tem (RN-290). */
  BEYOND_GRANTOR: 'ACCESS.ROLE_BEYOND_GRANTOR',
} as const;

/**
 * Os dois papéis de administração não se editam.
 *
 * O Super administrador leva o catálogo inteiro, e é o que garante que sempre exista
 * alguém capaz de consertar um papel mal configurado (RN-136): deixá-lo editável
 * permitiria tirar dele `roles.manage`, e aí ninguém mais abriria a tela de papéis para
 * devolver. O Administrador leva o que o código diz — tudo, menos `roles.manage` (RN-286)
 * — e fica pelo mesmo motivo: se a tela o deixasse crescer, o limite entre quem cuida
 * das pessoas e quem cuida dos papéis dependeria de ninguém clicar.
 */
export function isLockedRole(role: Pick<Role, 'key'>): boolean {
  return role.key === ROLE.SUPER_ADMIN || role.key === ROLE.ADMIN;
}

const CATALOG_ORDER = new Map(
  PERMISSION_CATALOG.map((definition, index) => [definition.key, index]),
);

/**
 * As permissões pedidas, completadas e conferidas.
 *
 * Completadas porque uma tela sem o que ela lê abriria vazia, e uma ação sem a leitura
 * do que ela altera não teria onde ser feita (`completePermissions`). Conferidas porque um
 * papel sem tela nenhuma entraria no sistema e não veria nada — e porque uma chave fora
 * do catálogo não é permissão, é erro de digitação.
 */
export function roleEntitlements(
  requested: readonly string[],
): Result<KnownPermissionKey[], DomainError> {
  const unknown = requested.filter((key) => !CATALOG_ORDER.has(key as KnownPermissionKey));
  if (unknown.length > 0) {
    return err(
      domainError(ROLE_ERROR.UNKNOWN_PERMISSION, 'Permissão desconhecida.', {
        permissions: unknown,
      }),
    );
  }

  const complete = withTabsOfBarePages(
    completePermissions(requested as readonly KnownPermissionKey[]),
  );
  if (pagesOpenedBy(complete).length === 0) {
    return err(
      domainError(
        ROLE_ERROR.WITHOUT_PAGE,
        'Marque ao menos uma tela — sem nenhuma, quem tiver este papel entra e não vê nada.',
      ),
    );
  }

  return ok(
    [...complete].sort((a, b) => (CATALOG_ORDER.get(a) ?? 0) - (CATALOG_ORDER.get(b) ?? 0)),
  );
}

export function createRole(input: {
  id: RoleId;
  key: string;
  name: string;
  description?: string;
  permissions: readonly string[];
  now: string;
}): Result<Role, DomainError> {
  const permissions = roleEntitlements(input.permissions);
  if (!permissions.ok) return permissions;

  const description = input.description?.trim();
  return ok({
    id: input.id,
    key: input.key,
    name: input.name.trim(),
    ...(description === undefined || description === '' ? {} : { description }),
    permissions: permissions.value,
    isSystem: false,
    customizedAt: input.now,
  });
}

export function editRole(
  role: Role,
  input: { name: string; description?: string; permissions: readonly string[]; now: string },
): Result<Role, DomainError> {
  if (isLockedRole(role)) {
    return err(
      domainError(
        ROLE_ERROR.LOCKED,
        'O Administrador tem sempre acesso completo e não pode ser editado.',
      ),
    );
  }

  const permissions = roleEntitlements(input.permissions);
  if (!permissions.ok) return permissions;

  const description = input.description?.trim();
  return ok({
    id: role.id,
    key: role.key,
    name: input.name.trim(),
    ...(description === undefined || description === '' ? {} : { description }),
    permissions: permissions.value,
    isSystem: role.isSystem,
    customizedAt: input.now,
  });
}

/**
 * A chave de um papel criado na tela, tirada do nome.
 *
 * A chave é o que a auditoria grava (`roleKey`) e o que o código usa para achar os papéis
 * de fábrica; o nome é o que a pessoa lê. Ela nasce do nome e **não acompanha** as
 * renomeações: a trilha de ontem continua apontando para o mesmo papel.
 */
export function roleKeyFromName(name: string, taken: ReadonlySet<string>): string {
  const base =
    name
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '') || 'PAPEL';

  let key = base;
  for (let suffix = 2; taken.has(key); suffix += 1) key = `${base}_${String(suffix)}`;
  return key;
}
